import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveUnits, fmtSpeed, fmtDistance, fmtElev, fmtDuration } from '@/lib/units';
import { decodePolyline } from '@/lib/polyline';
import {
  Activity, RefreshCw, Link2Off, Waves, Loader2, Gauge, Route, MapPin,
  TrendingUp, ChevronDown, ChevronUp, Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1) {
      map.fitBounds(points, { padding: [16, 16] });
    }
  }, [points, map]);
  return null;
};

const RouteMap = ({ polyline }) => {
  const { t } = useTranslation();
  const points = decodePolyline(polyline);
  if (points.length < 2) {
    return <div className="text-xs text-muted-foreground py-4 text-center">{t('stravaCard.noRoute')}</div>;
  }
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 mt-3" style={{ height: 220 }} data-testid="strava-route-map">
      <MapContainer center={points[0]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} attributionControl={false}>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
        <Polyline positions={points} pathOptions={{ color: '#FC4C02', weight: 4, opacity: 0.9 }} />
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
};

const SessionRow = ({ s, units, locale }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const date = s.start_date ? new Date(s.start_date).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const stats = [
    { icon: Clock, label: fmtDuration(s.moving_time_s) },
    { icon: Route, label: fmtDistance(s.distance_m, units) },
    { icon: Gauge, label: `${t('stravaCard.avg')} ${fmtSpeed(s.avg_speed_ms, units)}` },
    { icon: TrendingUp, label: `${t('stravaCard.max')} ${fmtSpeed(s.max_speed_ms, units)}` },
  ];
  const stravaActivityId = s.strava_activity_id || s.id;
  const stravaUrl = stravaActivityId ? `https://www.strava.com/activities/${stravaActivityId}` : null;
  return (
    <div className="rounded-xl bg-[#0A0A0A] border border-white/8 p-4" data-testid="strava-session-row">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#FC4C02]/12 flex items-center justify-center shrink-0"><Waves className="w-4 h-4 text-[#FC4C02]" /></div>
          <div className="min-w-0">
            <div className="font-medium truncate" data-testid="strava-session-name">{s.name || s.sport_type}</div>
            <div className="text-xs text-muted-foreground">{date} · {s.sport_type}{s.location ? ` · ${s.location}` : ''}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {stravaUrl && (
            <a
              href={stravaUrl}
              target="_blank"
              rel="noreferrer"
              title={t('stravaCard.viewOnStrava')}
              aria-label={t('stravaCard.viewOnStrava')}
              data-testid="strava-activity-link"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium text-[#FC4C02] hover:bg-[#FC4C02]/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC4C02]/50"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('stravaCard.viewOnStrava')}</span>
            </a>
          )}
          {s.polyline && (
            <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} className="text-muted-foreground hover:text-white shrink-0 h-8" data-testid="strava-route-toggle">
              <MapPin className="w-4 h-4 mr-1" /> {t('stravaCard.route')} {open ? <ChevronUp className="w-3.5 h-3.5 ml-0.5" /> : <ChevronDown className="w-3.5 h-3.5 ml-0.5" />}
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pl-12">
        {stats.map((st, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-sm text-white/85 font-data"><st.icon className="w-3.5 h-3.5 text-[#48CAE4]" /> {st.label}</span>
        ))}
        {s.elev_gain_m > 0 && <span className="inline-flex items-center gap-1.5 text-sm text-white/85 font-data"><TrendingUp className="w-3.5 h-3.5 text-[#48CAE4]" /> {fmtElev(s.elev_gain_m, units)}</span>}
      </div>
      {open && s.polyline && <div className="pl-12"><RouteMap polyline={s.polyline} /></div>}
    </div>
  );
};

export const StravaCard = () => {
  const { user, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const units = resolveUnits(user);
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const autoSyncedRef = useRef(false);

  const loadStatus = async () => {
    try { const { data } = await api.get('/strava/status'); setStatus(data); return data; }
    catch { return null; }
  };
  const loadSessions = async () => {
    try { const { data } = await api.get('/strava/sessions'); setSessions(data || []); }
    catch (e) { console.debug('strava sessions load failed', e); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const st = await loadStatus();
      if (st?.connected) {
        await loadSessions();
        // Auto-sync once when the dashboard opens (cheap: uses `after` filter server-side).
        if (!autoSyncedRef.current) { autoSyncedRef.current = true; sync(true); }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/strava/authorize-url?origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e, t('stravaCard.connectError')));
      setConnecting(false);
    }
  };

  const sync = async (silent = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post('/strava/sync');
      await loadSessions();
      await loadStatus();
      if (!silent) toast.success(t('stravaCard.syncedToast', { count: data.imported }));
    } catch (e) {
      if (!silent) toast.error(apiError(e, t('stravaCard.syncError')));
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post('/strava/disconnect');
      setSessions([]);
      await loadStatus();
      refreshUser();
      toast.success(t('stravaCard.disconnected'));
    } catch (e) {
      toast.error(apiError(e, t('stravaCard.disconnectError')));
    }
  };

  const StravaLogo = (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
      <Activity className="w-3.5 h-3.5 text-[#FC4C02]" /> {t('stravaCard.poweredBy')}
    </span>
  );

  if (loading) {
    return (
      <div className="kite-card p-5" data-testid="strava-card">
        <Skeleton className="h-6 w-32 bg-white/10" />
        <Skeleton className="h-10 w-full mt-4 bg-white/10" />
      </div>
    );
  }

  return (
    <div className="kite-card p-5" data-testid="strava-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FC4C02]/12 flex items-center justify-center"><Activity className="w-5 h-5 text-[#FC4C02]" /></div>
          <div>
            <h3 className="font-semibold">{t('stravaCard.title')}</h3>
            {status?.connected ? (
              <p className="text-xs text-muted-foreground" data-testid="strava-connected-meta">
                {t('stravaCard.connected')}{status.athlete?.firstname ? ` · ${status.athlete.firstname} ${status.athlete.lastname || ''}` : ''}
                {status.last_synced_at ? ` · ${t('stravaCard.syncedOn', { date: new Date(status.last_synced_at).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) })}` : ''}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{t('stravaCard.subtitle')}</p>
            )}
          </div>
        </div>
        {status?.connected && (
          <div className="flex items-center gap-2">
            <Button onClick={() => sync(false)} disabled={syncing} variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="strava-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> {t('stravaCard.syncNow')}</>}
            </Button>
            <Button onClick={disconnect} variant="ghost" size="icon" className="text-muted-foreground hover:text-[#FF6B35]" title={t('stravaCard.disconnectTitle')} data-testid="strava-disconnect-btn">
              <Link2Off className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Not configured yet (no API keys set) */}
      {!status?.configured && (
        <div className="mt-4 rounded-xl border border-white/10 bg-[#0A0A0A] p-4 text-sm text-muted-foreground" data-testid="strava-not-configured">
          {t('stravaCard.notConfigured')}
        </div>
      )}

      {/* Configured but not connected */}
      {status?.configured && !status?.connected && (
        <div className="mt-4">
          <p className="text-sm text-muted-foreground mb-3">{t('stravaCard.connectIntro')}</p>
          <Button onClick={connect} disabled={connecting} className="bg-[#FC4C02] hover:bg-[#e34402] text-white font-semibold rounded-xl h-11 px-5" data-testid="strava-connect-btn">
            {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />} {t('stravaCard.connectBtn')}
          </Button>
        </div>
      )}

      {/* Connected: session list */}
      {status?.connected && (
        <div className="mt-4">
          {sessions.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6 text-center" data-testid="strava-empty">
              <Waves className="w-8 h-8 text-[#48CAE4] mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">{t('stravaCard.emptyTitle')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((s) => <SessionRow key={s.id} s={s} units={units} locale={locale} />)}
            </div>
          )}
          <div className="mt-3 flex justify-end">{StravaLogo}</div>
        </div>
      )}
    </div>
  );
};
